import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchBuyerBookings,
  fetchListing,
  getMyBuyerAlias,
  createReview,
  fetchReviewByBooking,
  updateBookingStatus,
  type Booking,
  type Listing,
  type Review,
} from '../../features/marketplace/api';
import { getAllowedCredentials } from '../../lib/webauthn';

export default function MarketplaceBuyerPage() {
  const navigate = useNavigate();
  const [buyerAlias, setBuyerAlias] = useState<string>('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [listings, setListings] = useState<Record<string, Listing>>({});
  const [reviews, setReviews] = useState<Record<string, Review | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Review form state
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // QR verification state
  const [generatingQR, setGeneratingQR] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrGenerating, setQrGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select first completed booking that needs review
  useEffect(() => {
    if (!selectedBooking && bookings.length > 0) {
      const firstReviewableBooking = bookings.find(booking =>
        booking.status === 'completed' && !reviews[booking.id]
      );
      if (firstReviewableBooking) {
        setSelectedBooking(firstReviewableBooking.id);
      }
    }
  }, [bookings, reviews, selectedBooking]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Get buyer alias
      const aliasResp = await getMyBuyerAlias();
      if (!aliasResp.registered || !aliasResp.alias) {
        setError('no_alias'); // Special error code to trigger welcome screen
        setLoading(false);
        return;
      }

      const alias = aliasResp.alias.buyerAlias;
      setBuyerAlias(alias);

      // Fetch bookings
      const bookingsData = await fetchBuyerBookings(alias);
      setBookings(bookingsData || []);

      // Fetch listings for each booking
      const listingsMap: Record<string, Listing> = {};
      const reviewsMap: Record<string, Review | null> = {};

      for (const booking of bookingsData || []) {
        if (!listingsMap[booking.listingId]) {
          try {
            const listing = await fetchListing(booking.listingId);
            listingsMap[booking.listingId] = listing;
          } catch (err) {
            console.error(`Failed to load listing ${booking.listingId}:`, err);
          }
        }

        // Fetch existing review if any
        try {
          const existingReview = await fetchReviewByBooking(booking.id);
          reviewsMap[booking.id] = existingReview;
        } catch (err) {
          reviewsMap[booking.id] = null;
        }
      }

      setListings(listingsMap);
      setReviews(reviewsMap);
    } catch (err) {
      console.error('Failed to load buyer bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitReview(bookingId: string) {
    if (rating < 1 || rating > 5) {
      setSubmitError('Rating must be between 1 and 5');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const review = await createReview(bookingId, rating, comment, 'buyer_to_seller', buyerAlias);

      // Update reviews state
      setReviews(prev => ({
        ...prev,
        [bookingId]: review,
      }));

      // Check if seller has also reviewed - if so, mark booking as reviewed
      // This will be checked on backend, but we can also update locally
      // Note: We'll let the backend handle the status update when both reviews exist

      // Clear form
      setRating(5);
      setComment('');

      // Find next booking that needs review
      const nextReviewableBooking = bookings.find(booking =>
        booking.id !== bookingId &&
        booking.status === 'completed' &&
        !reviews[booking.id]
      );

      // Auto-select next reviewable booking or clear selection
      setSelectedBooking(nextReviewableBooking ? nextReviewableBooking.id : null);
    } catch (err) {
      console.error('Failed to submit review:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  }

  function getBuyerFriendlyStatus(status: string): string {
    switch (status) {
      case 'requested': return 'Pending';
      case 'confirmed': return 'Confirmed';
      case 'rejected': return 'Seller Cancelled';
      case 'cancelled': return 'User Cancelled';
      case 'no_show': return 'No Show';
      case 'verified': return 'In Progress';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'reviewed': return 'Reviewed';
      default: return status.replace('_', ' ');
    }
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'requested': return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800';
      case 'confirmed': return 'text-brand-700 dark:text-brand-400 bg-brand-100 dark:bg-brand-900/20 border-brand-300 dark:border-brand-800';
      case 'rejected': return 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-800';
      case 'no_show': return 'text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800';
      case 'verified': return 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-800';
      case 'in_progress': return 'text-violet-700 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/20 border-violet-300 dark:border-violet-800';
      case 'completed': return 'text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-800';
      case 'reviewed': return 'text-teal-700 dark:text-teal-400 bg-teal-100 dark:bg-teal-900/20 border-teal-300 dark:border-teal-800';
      case 'cancelled': return 'text-slate-700 dark:text-slate-400 bg-slate-100 dark:bg-slate-900/20 border-slate-300 dark:border-slate-800';
      default: return 'text-slate-700 dark:text-slate-400 bg-slate-100 dark:bg-slate-900/20 border-slate-300 dark:border-slate-800';
    }
  }

  function canReview(booking: Booking): boolean {
    return booking.status === 'completed' && !reviews[booking.id];
  }

  function canCancelBooking(booking: Booking): { canCancel: boolean; reason?: string } {
    // Can cancel if status is requested (pending)
    if (booking.status === 'requested') {
      return { canCancel: true };
    }

    // Can cancel if status is confirmed and more than 10 minutes before start time
    if (booking.status === 'confirmed') {
      const scheduledTime = new Date(booking.scheduledFor).getTime();
      const now = Date.now();
      const minutesUntilStart = (scheduledTime - now) / (1000 * 60);

      if (minutesUntilStart > 10) {
        return { canCancel: true };
      } else {
        return {
          canCancel: false,
          reason: `Cannot cancel - booking starts in ${Math.max(0, Math.round(minutesUntilStart))} minutes`
        };
      }
    }

    return { canCancel: false, reason: 'Booking cannot be cancelled at this stage' };
  }

  async function handleCancelBooking(bookingId: string) {
    if (!confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await updateBookingStatus(bookingId, 'cancelled');
      await loadData();
    } catch (err) {
      console.error('Failed to cancel booking:', err);
      alert(err instanceof Error ? err.message : 'Failed to cancel booking');
    }
  }

  async function handleGenerateVerificationQR(booking: Booking) {
    try {
      setQrGenerating(true);
      setGeneratingQR(booking.id);

      // Fetch allowed credentials from server to ensure only the logged-in user's credentials can be used
      const allowCredentials = await getAllowedCredentials();

      // Trigger WebAuthn signature on buyer's device
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: allowCredentials, // ✅ Restrict to logged-in user's credentials
          timeout: 60000,
          userVerification: 'required',
        },
      }) as PublicKeyCredential | null;

      if (!credential) {
        throw new Error('WebAuthn signature cancelled');
      }

      // Generate QR data with verification info
      const verificationData = {
        bookingId: booking.id,
        buyerAlias: buyerAlias,
        signedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes
        credentialId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        signature: 'webauthn_verified', // In production, include actual signature proof
      };

      // Create QR code data (JSON stringified)
      const qrString = JSON.stringify(verificationData);
      setQrData(qrString);
    } catch (err) {
      console.error('Failed to generate QR:', err);
      alert(err instanceof Error ? err.message : 'Failed to generate verification QR');
      setGeneratingQR(null);
    } finally {
      setQrGenerating(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center text-slate-600 dark:text-slate-400">Loading your bookings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-800 rounded-xl p-8 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Welcome to Marketplace!</h2>
          <p className="text-lg text-slate-700 dark:text-slate-300 mb-2">You don't have a buyer alias yet.</p>
          <p className="text-base text-slate-600 dark:text-slate-400 mb-6">
            No worries! When you book a service for the first time, you'll be prompted to create your buyer alias automatically.
            It only takes a few seconds!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/marketplace')}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Browse Marketplace & Book Services
            </button>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-4">
            💡 Your buyer alias will be your unique identifier for all bookings
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">My Bookings</h1>
        <p className="text-slate-600 dark:text-slate-400">Buyer Alias: {buyerAlias}</p>
      </div>

      {/* Status Filter */}
      {bookings.length > 0 && (
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-brand-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            All ({bookings.length})
          </button>
          <button
            onClick={() => setStatusFilter('requested')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'requested'
                ? 'bg-yellow-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            Pending ({bookings.filter(b => b.status === 'requested').length})
          </button>
          <button
            onClick={() => setStatusFilter('confirmed')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'confirmed'
                ? 'bg-brand-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            Confirmed ({bookings.filter(b => b.status === 'confirmed').length})
          </button>
          <button
            onClick={() => setStatusFilter('in_progress_combined')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'in_progress_combined'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            In Progress ({bookings.filter(b => b.status === 'verified' || b.status === 'in_progress').length})
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'completed'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            Completed ({bookings.filter(b => b.status === 'completed').length})
          </button>
          <button
            onClick={() => setStatusFilter('reviewed')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'reviewed'
                ? 'bg-teal-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            Reviewed ({bookings.filter(b => b.status === 'reviewed').length})
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            Seller Cancelled ({bookings.filter(b => b.status === 'rejected').length})
          </button>
          <button
            onClick={() => setStatusFilter('cancelled')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              statusFilter === 'cancelled'
                ? 'bg-slate-600 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
            }`}
          >
            User Cancelled ({bookings.filter(b => b.status === 'cancelled').length})
          </button>
        </div>
      )}

      {bookings.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
          <p className="text-slate-600 dark:text-slate-400">You haven't made any bookings yet.</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded"
          >
            Browse Marketplace
          </button>
        </div>
      ) : bookings.filter(booking => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'in_progress_combined') return booking.status === 'verified' || booking.status === 'in_progress';
        return booking.status === statusFilter;
      }).length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
          <p className="text-slate-600 dark:text-slate-400">No bookings with status: {statusFilter === 'in_progress_combined' ? 'In Progress' : getBuyerFriendlyStatus(statusFilter)}</p>
          <button
            onClick={() => setStatusFilter('all')}
            className="mt-4 px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded"
          >
            Show All Bookings
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings
            .filter(booking => {
              if (statusFilter === 'all') return true;
              if (statusFilter === 'in_progress_combined') return booking.status === 'verified' || booking.status === 'in_progress';
              return booking.status === statusFilter;
            })
            .map((booking) => {
            const listing = listings[booking.listingId];
            const review = reviews[booking.id];
            const isReviewing = selectedBooking === booking.id;

            return (
              <div key={booking.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                      {listing ? listing.title : 'Loading...'}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                      Seller: {listing ? listing.sellerAlias : '...'}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Scheduled: {new Date(booking.scheduledFor).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                      {getBuyerFriendlyStatus(booking.status)}
                    </span>
                  </div>
                </div>

                {listing && (
                  <div className="mb-4 text-sm text-slate-700 dark:text-slate-300">
                    <p className="line-clamp-2">{listing.description}</p>
                    {listing.durationMinutes && (
                      <p className="text-slate-600 dark:text-slate-400 mt-2">
                        Duration: {listing.durationMinutes} minutes
                      </p>
                    )}
                  </div>
                )}

                {/* Existing Review */}
                {review && (
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-yellow-400 text-lg">
                        {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                      </span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300">{review.comment}</p>
                  </div>
                )}

                {/* Pending Review Indicator */}
                {canReview(booking) && !isReviewing && (
                  <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-600 dark:text-yellow-400">⏳</span>
                        <span className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Review pending</span>
                      </div>
                      <span className="text-xs text-yellow-600 dark:text-yellow-400/70">Will be prompted next</span>
                    </div>
                  </div>
                )}

                {/* Review Form - Required for completed bookings */}
                {canReview(booking) && isReviewing && (
                  <div className="bg-brand-50 dark:bg-brand-900/20 border-2 border-brand-500 dark:border-brand-600 rounded p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-brand-600 dark:text-brand-400 text-lg">⚠️</span>
                      <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Review Required</h4>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">Please share your experience to help other users and complete this booking.</p>

                    {/* Star Rating */}
                    <div className="mb-4">
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-2">Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setRating(star)}
                            className="text-3xl focus:outline-none hover:scale-110 transition-transform"
                          >
                            <span className={star <= rating ? 'text-yellow-400' : 'text-slate-300 dark:text-slate-600'}>
                              ★
                            </span>
                          </button>
                        ))}
                        <span className="ml-2 text-slate-600 dark:text-slate-400 self-center">
                          {rating} / 5
                        </span>
                      </div>
                    </div>

                    {/* Comment */}
                    <div className="mb-4">
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-2">Comment</label>
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Share your experience..."
                        rows={4}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>

                    {submitError && (
                      <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded p-3 mb-4">
                        <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleSubmitReview(booking.id)}
                        disabled={submitting || !comment.trim()}
                        className="w-full px-4 py-3 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-400 dark:disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded font-semibold"
                      >
                        {submitting ? 'Submitting...' : 'Submit Review'}
                      </button>
                      {!comment.trim() && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 text-center">Please add a comment to submit your review</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Cancel Booking Button */}
                {(() => {
                  const cancelCheck = canCancelBooking(booking);
                  if (cancelCheck.canCancel) {
                    return (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium"
                        >
                          Cancel Booking
                        </button>
                      </div>
                    );
                  } else if (cancelCheck.reason && (booking.status === 'requested' || booking.status === 'confirmed')) {
                    return (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded p-3">
                          <p className="text-xs text-slate-600 dark:text-slate-400">{cancelCheck.reason}</p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Generate Verification QR - Only during active service */}
                {(booking.status === 'in_progress' || booking.status === 'verified') && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => handleGenerateVerificationQR(booking)}
                      disabled={qrGenerating}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 dark:disabled:bg-slate-600 text-white rounded text-sm font-medium"
                    >
                      {qrGenerating && generatingQR === booking.id ? 'Generating...' : '🔐 Generate Verification QR'}
                    </button>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                      Generate QR code to show seller when ending service
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* QR Code Display Modal */}
      {qrData && generatingQR && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg border-2 border-green-600 max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-green-400 text-2xl">📱</span>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">Verification QR Code</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Show this QR code to the seller to verify service completion
            </p>

            {/* QR Code Display */}
            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="flex items-center justify-center">
                {/* Using QR Server API for now - in production, use a proper QR library */}
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`}
                  alt="Verification QR Code"
                  className="w-full h-auto max-w-[300px]"
                />
              </div>
            </div>

            {/* Info */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded p-3 mb-4">
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Booking ID:</span>
                  <span className="text-slate-900 dark:text-slate-200 font-mono">{generatingQR.substring(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Buyer:</span>
                  <span className="text-slate-900 dark:text-slate-200">{buyerAlias}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Expires:</span>
                  <span className="text-slate-900 dark:text-slate-200">15 minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Status:</span>
                  <span className="text-green-600 dark:text-green-400">✓ Signed with WebAuthn</span>
                </div>
              </div>
            </div>

            {/* Copyable QR Data */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                QR Data (for manual input)
              </label>
              <div className="relative">
                <textarea
                  readOnly
                  value={qrData}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 text-xs font-mono resize-none"
                  rows={4}
                  onClick={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(qrData);
                    alert('QR data copied to clipboard!');
                  }}
                  className="absolute top-2 right-2 px-3 py-1 bg-brand-600 hover:bg-brand-500 text-white text-xs rounded"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-500 mt-1">
                Seller can manually paste this if camera scanning doesn't work
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setQrData(null);
                setGeneratingQR(null);
              }}
              className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
